import Vue from 'vue';
import resetState from '@/helpers/resetState';
import { getTransactionPromise } from '@/helpers/transaction';
import store from '@/store';
import { ABI } from './ABI';
import { formatData } from '@/constants/commonContent';
import { messages } from '@/constants/messages';
import { Contract } from 'web3-eth-contract';

const FEATURE_NAME = 'TokenFeature';

let from;

async function contract(): Promise<Contract> {
  from = {from: store.getters.getWallet};
  const address = store.getters.getFeatureAddress(FEATURE_NAME);
  return new Promise(resolve => {
    const contract = new window.web3.eth.Contract(ABI, address);
    resolve(contract);
  });
}

const initialState = {
  tokenName: '',
  tokenSymbol: '',
  tokenTotalSupply: 0,
  tokenLockedValue: null,
  tokenBalances: {},
  isInitiatedToken: true,
  tokenTransferCounter: 0,
  tokenTransfers: {},
  freezedOf: null
};

const state = {
  tokenName: initialState.tokenName,
  tokenSymbol: initialState.tokenSymbol,
  tokenTotalSupply: initialState.tokenTotalSupply,
  tokenLockedValue: initialState.tokenLockedValue,
  tokenBalances: initialState.tokenBalances,
  isInitiatedToken: initialState.isInitiatedToken,
  tokenTransferCounter: initialState.tokenTransferCounter,
  tokenTransfers: initialState.tokenTransfers,
  freezedOf: initialState.freezedOf
};

const getters = {
  getTokenName: state => state.tokenName,
  getTokenSymbol: state => state.tokenSymbol,
  getTokenTotalSupply: state => state.tokenTotalSupply,
  getTokenBalance: (state, getters) => address => {
    let walletAddress = address;
    if (!address) walletAddress = getters.getWallet;
    return state.tokenBalances[walletAddress];
  },
  getInitStatusToken: state => state.isInitiatedToken,
  getTokenTransferCounter: state => state.tokenTransferCounter,
  getTokenTransfers: state => state.tokenTransfers,
  getTokenInfo: (state, getters) => ({
    name: getters.getTokenName,
    symbol: getters.getTokenSymbol,
    firstSymbol: getters.getTokenName.substring(0, 1),
    noFirstSymbol: getters.getTokenName.substring(1),
    totalSupply: getters.getTokenTotalSupply
  }),
  getFreezedOf: state => state.freezedOf
};

const mutations = {
  setTokenName: (state, item) => {
    state.tokenName = item;
  },
  setTokenSymbol: (state, item) => {
    state.tokenSymbol = item;
  },
  setTokenTotalSupply: (state, item) => {
    state.tokenTotalSupply = item;
  },
  setTokenBalance: (state, item) => {
    Vue.set(state.tokenBalances, item.address, item.balance);
  },
  setInitStatusToken: (state, item) => {
    state.isInitiatedToken = item;
  },
  setTokenTransferCounter: (state, item) => {
    state.tokenTransferCounter = item;
  },
  setTokenTransfer: (state, item) => {
    Vue.set(state.tokenTransfers, item.id, item);
  },
  setFreezedOf: (state, item) => {
    state.freezedOf = item;
  },
  resetTokenFeature: state => {
    resetState(state, initialState);
  }
};

const actions = {
  initTokenFeature({ dispatch, commit, getters }) {
    dispatch('isInitiatedTokenDapp');
    dispatch('getTokenNameDapp');
    dispatch('getTokenSymbolDapp');
    dispatch('getTokenTotalSupplyDapp');
    dispatch('getTransfersDapp');
    dispatch('getFreezedOfDapp', getters.getWallet);

    //Events
    contract().then(contract => {
      let transactionHash = '0x';
      contract.events.Transfer().subscribe((err, res) => {
        if (!err && transactionHash !== res.transactionHash) {
          transactionHash = res.transactionHash;
          dispatch('getTransfersDapp').then(() => {
            dispatch('getBalancesDapp');
            dispatch('getCompanyInfoDapp');
            if (res.returnValues.from === getters.getWallet) {
              commit('companyTreasuryWalletPending', false);
              commit('transferTokenPending', false);
              commit('resetMailPending', 'transfersShares');
            }
            commit('resetMailPending', 'transfersShares');
            commit('transferTokenPending', false);
          });
        }
      });
    });
  },
  /**
   * Send token to another member
   * @param to {string}
   * @param value {number}
   * @res "transaction hash" {string}
   */
  transferDapp({ dispatch, commit, getters }, data) {
    console.log(data);
    dispatch('countFeesDapp', data.value).then(fee => {
      contract().then(contract => {
        contract.methods.transfer(data.to, data.value)
          .send({...from, value: fee}, (err, res) => {
            if (err) {
              commit('transferTokenPending', false);
              Vue.toasted.error(messages.canceledTransfer);
            } else {
              dispatch('sendTransferMailExt', {
                from: getters.getWallet,
                to: data.to,
                amount: `${data.value} ${getters.getTokenSymbol}`,
                time: Vue.moment().format(formatData.dateTime),
                hasEscrow: getters.isUsedEscrow,
                tx: res
              });
            }
          });
      });
    });
  },
  /**
   * Get token name
   * @res "token name" {string}
   */
  getTokenNameDapp({ commit }) {
    contract().then(contract => {
      contract.methods.name()
        .call()
        .then(res => {
          commit('setTokenName', res);
        })
        .catch(console.error);
    });
  },
  /**
   * Get token symbol
   * @res "token symbol" {string}
   */
  getTokenSymbolDapp({ commit }) {
    contract().then(contract => {
      contract.methods.symbol()
        .call()
        .then(res => {
          commit('setTokenSymbol', res);
        })
        .catch(console.error);
    });
  },
  /**
   * Get token total supply
   * @res "token total supply" {number}
   */
  getTokenTotalSupplyDapp({ commit }) {
    contract().then(contract => {
      contract.methods.totalSupply()
        .call()
        .then(res => {
          commit('setTokenTotalSupply', res);
        })
        .catch(console.error);
    });
  },
  /**
   * Initialization info for token
   * @param tokenName {string}
   * @param tokenSymbol {string}
   * @param tokenTotalsupply {number}
   * @param votingPercents {number}
   * @return {Promise<void>}
   */
  initiateParametersDapp({}, data) {
    return new Promise((resolve, reject) => {
      contract().then(contract => {
        contract.methods.initiateParameters(
          data.tokenName,
          data.tokenSymbol,
          data.tokenTotalsupply,
          data.votingPercents)
          .send(from, (err, res) => {
            return getTransactionPromise(resolve, reject, err, res);
          }
        );
      });
    });
  },
  /**
   * Is the token initialized?
   */
  isInitiatedTokenDapp({ commit }) {
    contract().then(contract => {
      contract.methods.isInitiatedToken()
        .call()
        .then(res => {
          commit('setInitStatusToken', res);
        })
        .catch(console.error);
    });
  },
  /**
   * Get balance for token wallet
   * @param address {string}
   * @res "balance" {number}
   */
  getBalanceDapp({ getters, commit }, addr) {
    contract().then(contract => {
      contract.methods.balanceOf(addr)
        .call()
        .then(res => {
          let balance = parseInt(res);
          if (addr === getters.getWallet && balance === 0) {
            window.router.push('/');
            window.location.reload();
          }
          commit('setTokenBalance', {
            address: addr,
            balance
          });
        })
        .catch(console.error);
    });
  },
  getBalancesDapp({ dispatch, getters }) {
    for (let addr in getters.getMembers) {
      dispatch('getBalanceDapp', addr);
    }
  },
  /**
   * Get token transfer
   * @param id {number}
   * @res[0] "id" {number}
   * @res[1] "from" {string}
   * @res[2] "to" {string}
   * @res[3] "senderbalance" {number}
   * @res[4] "receiverbalance" {number}
   * @res[5] "value" {number}
   * @res[6] "date" {number}
   * @res[7] "mode" {number}
   * @res[8] "resultTime" {number}
   * @res[9] "status" {number}
   */
  getTransferDapp({ commit }, id) {
    contract().then(contract => {
      contract.methods.getTransfer(id)
        .call()
        .then(res => {
          let data = {
            id: res[0],
            from: res[1],
            to: res[2],
            senderbalance: res[3],
            receiverbalance: res[4],
            value: res[5],
            date: parseInt(res[6]),
            mode: parseInt(res[7]),
            resultTime: parseInt(res[8]),
            status: parseInt(res[9])
          };
          commit('setTokenTransfer', data);
        })
        .catch(err => console.error(err));
    });
  },
  getTransfersDapp({ dispatch }) {
    dispatch('getTransferCounterDapp').then(result => {
      for (let i = 0; i < result; i++) {
        dispatch('getTransferDapp', i);
      }
    });
  },
  /**
   * Get token transfer count
   * @return {Promise<number>}
   * @res "count" {number}
   */
  getTransferCounterDapp({ commit }) {
    return new Promise(resolve => {
      contract().then(contract => {
        contract.methods.getTransferCount()
          .call()
          .then(res => {
            commit('setTokenTransferCounter', res);
            resolve(res);
          })
          .catch(console.error);
      });
    });
  },
  /**
   * Get the number of frozen tokens
   * @res "count" {number}
   */
  getFreezedOfDapp({ commit }, address) {
    contract().then(contract => {
      contract.methods.freezedOf(address)
        .call()
        .then(res => {
          commit('setFreezedOf', parseInt(res));
        })
        .catch(console.error);
    });
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
